import React from "react";
import ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/react";
import "@/styles/tokens.css";
import "@/styles/app.css";
import "@/styles/campaign_studio.css";
import "@/styles/cpp_tester.css";
import "@/styles/autopilot_suggestions.css";
import "@/styles/dkg.css";
import "@/styles/btse.css";
import "@/styles/auth.css";
import "@/styles/keyword_parser.css";
import "@/styles/aso.css";
import "@/styles/ad_account.css";
import "@/styles/ads_filter.css";
import { App } from "@/App";
import { ErrorFallback } from "@/components/ErrorFallback";
import { reloadForStaleChunk } from "@/lib/chunk-reload";

// ---------------------------------------------------------------------------
// Stale-tab chunk self-heal (global catch-all). Vite fires
// `vite:preloadError` whenever a dynamically-imported / module-preloaded
// chunk fails to load — which happens to any tab left open across a
// deploy (the hashed chunk it references was deleted by the new build).
// This covers EVERYTHING, not just the route-level lazy imports that
// `lazyWithReload` wraps: a chunk pulled when opening a modal (e.g. the
// Rules "Apply To" popup, Campaign Studio's NameTemplateModal), a vendor
// split, a modulepreload, etc. Reload once to pull the fresh index.html
// + current chunk hashes. The guard is the SHARED self-expiring
// timestamp in @/lib/chunk-reload — NOT a sticky boolean. The old sticky
// `oneads.chunk_reloaded` flag was never cleared, so after the first
// reload in a tab session this handler swallowed the error's default
// (preventDefault) but stopped reloading — leaving the broken state
// visible for the ~1s until another mechanism raced in. That was the
// "shows an error, then reloads" symptom (CLAUDE.md §56.3).
window.addEventListener("vite:preloadError", (e) => {
  e.preventDefault(); // stop the default uncaught-rejection surfacing
  reloadForStaleChunk();
});

// ---------------------------------------------------------------------------
// Sentry — initialised before the app renders so any boot-time errors
// (lazy-loaded chunk failure, blocked CSS, malformed bootstrap blob)
// reach us instead of vanishing into the browser console.
//
// VITE_SENTRY_DSN is baked at build time by Vite — no runtime fetch
// of the DSN. Empty / unset = SDK does nothing (local dev, tests).
// VITE_SENTRY_RELEASE = git SHA of the deploy build, set by the CI
// workflow. Sentry uses it to attribute each issue to the exact
// commit and to power "first seen / last seen / regressed in
// release X" markers.
//
// `tracePropagationTargets` lets the React app stitch its frontend
// transactions to the backend's transactions when both have SDKs
// initialised — clicking a frontend error then shows the backend
// trace inline.
// ---------------------------------------------------------------------------
const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
if (dsn) {
  Sentry.init({
    dsn,
    environment:
      (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ||
      (import.meta.env.PROD ? "production" : "development"),
    release: import.meta.env.VITE_SENTRY_RELEASE as string | undefined,
    integrations: [
      Sentry.browserTracingIntegration(),
      // Replay disabled by default — burns free-tier quota fast and
      // we don't yet have a workflow that needs visual playback.
      // Enable later via Sentry.replayIntegration() if the team
      // wants user-session replays for hard-to-reproduce bugs.
    ],
    // 10 % of page loads / fetches — spots slow API calls without
    // burning the Developer-plan quota.
    tracesSampleRate: 0.1,
    // Connect frontend traces to backend traces when both call the
    // same host. Same-origin requests are always traced; explicit
    // list is for cases where the API host differs from page host.
    tracePropagationTargets: [
      /^https?:\/\/(new|staging)\.oneads\.pro/,
    ],
    // Drop literal PII from outgoing events. send_default_pii=false
    // already skips IP / user-agent / cookies — this scrub catches
    // anything that ended up in form bodies / URL state / a custom
    // event payload.
    sendDefaultPii: false,
    beforeSend(event) {
      _scrubPii(event as unknown as Record<string, unknown>);
      return event;
    },
  });
}

// Walk an event payload and scrub values for known-sensitive keys.
// Mirrors `app/main.py::_sentry_before_send` so backend + frontend
// give Sentry consistent redaction.
const PII_KEYS = new Set([
  "email",
  "password",
  "token",
  "access_token",
  "refresh_token",
  "jwt",
  "authorization",
  "cookie",
  "set-cookie",
]);
function _scrubPii(obj: unknown): void {
  if (obj && typeof obj === "object" && !Array.isArray(obj)) {
    const dict = obj as Record<string, unknown>;
    for (const key of Object.keys(dict)) {
      if (PII_KEYS.has(key.toLowerCase())) {
        dict[key] = "[scrubbed]";
      } else {
        _scrubPii(dict[key]);
      }
    }
  } else if (Array.isArray(obj)) {
    for (const v of obj) _scrubPii(v);
  }
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    {/* Sentry's ErrorBoundary catches render-time exceptions anywhere
        below it and reports them — without it, React's default
        behaviour is to unmount the tree and show a blank page that
        only console.errors. The fallback distinguishes a stale-chunk
        404 (silent self-heal) from a real crash (see ErrorFallback). */}
    <Sentry.ErrorBoundary
      fallback={(props) => <ErrorFallback {...props} />}
    >
      <App />
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);
